import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'contexts/AuthContext';
import { ComparisonGrid } from 'components/ComparisonGrid';
import { AddToComparisonSimpleSelect } from 'components/ComparisonGrid/components';
import {
    useCreateReport, useGetComparisonReport, useUpdateReport, useDealSelectionManager,
} from 'views/Reports/hooks';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { URLS } from 'services/urls';
import useToast from 'hooks/useToast';
import { excelExport } from 'bricks/utils';
import { NSLoadingScreen } from 'bricks';
import { useComparisonGridConfig } from 'views/Reports/ReportTypes/ComparisonReport/hooks';
import { ReportHeader } from '../../ReportHeader/ReportHeader';
import {
    ICreateReportPostData, ReportTypeId, IReportDetails, IDealSelection,
} from '../../types';

import { MAX_DEALS_FOR_PDF_EXPORT } from './constants';
import { ExportOptions } from '../../constants';
import { formatExcelExportData } from './helpers';

export interface IProps {
    report?: IReportDetails;
    onClickBack: () => void;
    isEditing?: boolean;
}

export const ComparisonReport = ({ report, onClickBack, isEditing }: IProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { selectedOrganizationId } = useAuthContext();
    const { showSuccess, showError } = useToast();

    const [isEditMode, setIsEditMode] = useState(Boolean(!report || isEditing));
    const [reportName, setReportName] = useState(report?.name || 'New Comparison Report');
    const [reportDescription, setReportDescription] = useState(report?.description || '');

    // once everything uses the deal selection manager, this can be replaced with just the provided data
    const defaultSelectedDeals = useMemo(() => {
        if (report?.deals) {
            return report.deals.map(deal => ({
                deal: {
                    id: deal.dealId,
                    dealName: deal.dealName,
                    dealType: deal.dealType,
                    defaultProFormaId: deal.proFormaId,
                    project: 'Placeholder Project',
                    projectType: 'Placeholder DealProjectType',
                },
                proFormaId: deal.proFormaId,
            }));
        }
        if (location.state?.selectedDeals) {
            const selections = location.state.selectedDeals as IDealSelection[];
            return selections.map(selection => ({
                deal: {
                    ...selection.deal,
                    defaultProFormaId: selection.proFormaId,
                },
                proFormaId: selection.proFormaId,
            }));
        }
        return [];
    }, [report, location.state]);

    const {
        dealOptions, selectedDeals, addSelectedDeal, removeSelectedDeal, updateSelectedProFormaId, restoreInitialSelection,
    } = useDealSelectionManager(
        selectedOrganizationId!,
        defaultSelectedDeals as IDealSelection[],
    );

    const { data: comparisonReportData, isLoading: isFetchingComparisonReport, isRefetching: isRefetchingComparisonReport } = useGetComparisonReport(
        {
            orgId: selectedOrganizationId!,
            proFormaIds: selectedDeals?.map(deal => deal.proFormaId),
        },
        { enabled: selectedDeals?.length > 0 },
    );

    const effectiveDealOptions = useMemo(() => {
        if (!comparisonReportData) return dealOptions;
        const displayedDealIds = new Set(comparisonReportData.map(d => d.dealId));
        return dealOptions.map(option => ({
            ...option,
            isChecked: displayedDealIds.has(option.value),
            disabled: displayedDealIds.has(option.value),
        }));
    }, [dealOptions, comparisonReportData]);

    const gridConfig = useComparisonGridConfig(
        comparisonReportData,
        selectedDeals,
        isEditMode,
        isFetchingComparisonReport || isRefetchingComparisonReport,
        removeSelectedDeal,
        updateSelectedProFormaId,
    );

    const { mutate: createReport } = useCreateReport();
    const { mutate: updateReport } = useUpdateReport();

    const handleSaveReport = () => {
        const postData: ICreateReportPostData = {
            name: reportName,
            description: reportDescription,
            type: ReportTypeId.Comparison,
            deals: selectedDeals?.map(selection => ({
                dealId: selection.deal.id,
                proFormaId: selection.proFormaId,
            })),
        };
        if (report) {
            updateReport(
                {
                    postData: {
                        report_id: report.id,
                        ...postData,
                    },
                    orgId: selectedOrganizationId!,
                },
                {
                    onSuccess: data => {
                        queryClient.invalidateQueries({ queryKey: ['reportDetails', data.id] });
                        showSuccess('Report updated successfully');
                        setIsEditMode(false);
                    },
                    onError: () => {
                        showError('Failed to update report. Please try again.');
                    },
                },
            );
        } else {
            createReport(
                {
                    postData,
                    orgId: selectedOrganizationId!,
                },
                {
                    onSuccess: data => {
                        const path = generatePath(URLS.REPORTS.REPORT_PAGE, { reportId: data.id });
                        showSuccess('Report created successfully');
                        navigate(path);
                    },
                    onError: () => {
                        showError('Failed to create report. Please try again.');
                    },
                },
            );
        }
    };

    const exportOptions = useMemo(() => [
        {
            label: ExportOptions.PDF,
            value: ExportOptions.PDF,
            disabled: selectedDeals?.length > MAX_DEALS_FOR_PDF_EXPORT,
            disabledTooltip: `A maximum of ${MAX_DEALS_FOR_PDF_EXPORT} deals can be exported`,
        },
        { label: ExportOptions.EXCEL, value: ExportOptions.EXCEL },
    ], [selectedDeals?.length]);

    const onClickExport = () => {
        if (report && comparisonReportData) {
            const formattedExcelExportData = formatExcelExportData(report.name, report.description, comparisonReportData!);
            excelExport(report.name, false, formattedExcelExportData, true, []);
        }
    };

    const onClickCancel = () => {
        setIsEditMode(false);
        if (report) {
            setReportName(report.name);
            setReportDescription(report.description || '');
        }
        restoreInitialSelection();
    };

    const addToComparison = useCallback(() => {
        if (isFetchingComparisonReport || isRefetchingComparisonReport) {
            return <NSLoadingScreen />;
        }
        return (
            <AddToComparisonSimpleSelect
                label="Add to comparison"
                placeholder="Select a deal"
                options={effectiveDealOptions}
                onSelectOption={addSelectedDeal}
            />
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFetchingComparisonReport, isRefetchingComparisonReport, effectiveDealOptions]);

    return (
        <div data-testid="comparison-report-container">
            <ReportHeader
                reportId={report?.id}
                reportName={reportName}
                reportDescription={reportDescription}
                setReportName={setReportName}
                setReportDescription={setReportDescription}
                onClickBack={onClickBack}
                onClickExport={onClickExport}
                onClickSave={handleSaveReport}
                onClickCancel={onClickCancel}
                onClickEdit={() => setIsEditMode(true)}
                isEditing={isEditMode}
                exportOptions={exportOptions}
            />
            {isFetchingComparisonReport ? <NSLoadingScreen /> : (
                <ComparisonGrid
                    maxHeight="78vh"
                    gridConfig={gridConfig}
                    isEditMode={isEditMode}
                    renderAddToComparison={!effectiveDealOptions?.every(d => d.isChecked) ? addToComparison : null}
                />
            )}
        </div>
    );
};

export default ComparisonReport;
